import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  AdmissionsQuery,
  CreateAdmissionBody,
  CreateOffboardingBody,
  HrAnnouncementsQuery,
  HrBatchDocumentsBody,
  HrCreateAnnouncementBody,
  HrCreateDocumentBody,
  HrDistributeDocumentBody,
  HrDocumentsQuery,
  HrRequestsQuery,
  HrRespondBody,
  HrVacationsQuery,
  OffboardingsQuery,
} from "@evolua/api-client";
import { getApiClient } from "../auth/http.js";
import { useSession } from "../session/SessionContext.js";
import {
  createDemoHrAdmission,
  getDemoHrAdmission,
  handoffDemoHrAdmission,
  listDemoHrAdmissions,
  validateDemoHrAdmissionItem,
} from "./demo-admissions.js";
import {
  completeDemoHrOffboarding,
  completeDemoHrOffboardingItem,
  createDemoHrOffboarding,
  getDemoHrOffboarding,
  listDemoHrOffboardings,
  returnDemoHrOffboardingAsset,
  revokeDemoHrOffboardingAccess,
  saveDemoHrOffboardingInterview,
} from "./demo-offboardings.js";
import {
  approveDemoHrRequest,
  approveDemoHrVacationOnBehalf,
  assignDemoHrRequest,
  batchDemoHrDocuments,
  createDemoHrAnnouncement,
  createDemoHrDocument,
  distributeDemoHrDocument,
  getDemoHrAnnouncementReach,
  getDemoHrMetrics,
  getDemoHrRequest,
  listDemoHrAnnouncements,
  listDemoHrDocumentAccesses,
  listDemoHrDocuments,
  listDemoHrRequests,
  listDemoHrVacations,
  publishDemoHrAnnouncement,
  reassignDemoHrRequest,
  rejectDemoHrRequest,
  respondDemoHrRequest,
} from "./demo-hr.js";
import { queryKeys } from "./query-keys.js";

/** Fila de solicitações do RH (FASE-06-T49) + férias (T50). */

export function useHrRequests(filters: HrRequestsQuery = {}) {
  const { isDemoMode } = useSession();
  return useQuery({
    queryKey: queryKeys.hr.requests(filters),
    queryFn: () =>
      isDemoMode ? listDemoHrRequests(filters) : getApiClient().listHrRequests(filters),
  });
}

export function useHrRequest(id: string | null) {
  const { isDemoMode } = useSession();
  return useQuery({
    queryKey: queryKeys.hr.request(id ?? ""),
    enabled: Boolean(id),
    queryFn: () =>
      isDemoMode ? getDemoHrRequest(id!) : getApiClient().getHrRequest(id!),
  });
}

export function useHrMetrics() {
  const { isDemoMode } = useSession();
  return useQuery({
    queryKey: queryKeys.hr.metrics(),
    queryFn: () =>
      isDemoMode ? getDemoHrMetrics() : getApiClient().getHrRequestMetrics(),
  });
}

function invalidateHr(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: queryKeys.hr.all });
}

export function useAssignHrRequest() {
  const { isDemoMode } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; assigneeSubject?: string; comment?: string }) =>
      isDemoMode
        ? Promise.resolve(
            assignDemoHrRequest(input.id, {
              ...(input.assigneeSubject
                ? { assigneeSubject: input.assigneeSubject }
                : {}),
              ...(input.comment ? { comment: input.comment } : {}),
            }),
          )
        : getApiClient().assignHrRequest(input.id, {
            ...(input.assigneeSubject ? { assigneeSubject: input.assigneeSubject } : {}),
            ...(input.comment ? { comment: input.comment } : {}),
          }),
    onSuccess: () => invalidateHr(queryClient),
  });
}

export function useReassignHrRequest() {
  const { isDemoMode } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; assigneeSubject: string; reason: string }) =>
      isDemoMode
        ? Promise.resolve(
            reassignDemoHrRequest(input.id, {
              assigneeSubject: input.assigneeSubject,
              reason: input.reason,
            }),
          )
        : getApiClient().reassignHrRequest(input.id, {
            assigneeSubject: input.assigneeSubject,
            reason: input.reason,
          }),
    onSuccess: () => invalidateHr(queryClient),
  });
}

export function useRespondHrRequest() {
  const { isDemoMode } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; body: HrRespondBody }) =>
      isDemoMode
        ? Promise.resolve(respondDemoHrRequest(input.id, input.body))
        : getApiClient().respondHrRequest(input.id, input.body),
    onSuccess: () => invalidateHr(queryClient),
  });
}

export function useApproveHrRequest() {
  const { isDemoMode } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; comment?: string }) =>
      isDemoMode
        ? Promise.resolve(
            approveDemoHrRequest(input.id, {
              ...(input.comment ? { comment: input.comment } : {}),
            }),
          )
        : getApiClient().approveHrRequest(input.id, {
            ...(input.comment ? { comment: input.comment } : {}),
          }),
    onSuccess: () => invalidateHr(queryClient),
  });
}

export function useRejectHrRequest() {
  const { isDemoMode } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; comment: string }) =>
      isDemoMode
        ? Promise.resolve(rejectDemoHrRequest(input.id, { comment: input.comment }))
        : getApiClient().rejectHrRequest(input.id, { comment: input.comment }),
    onSuccess: () => invalidateHr(queryClient),
  });
}

export function useHrVacations(filters: HrVacationsQuery = {}) {
  const { isDemoMode } = useSession();
  return useQuery({
    queryKey: queryKeys.hr.vacations(filters),
    queryFn: () =>
      isDemoMode ? listDemoHrVacations(filters) : getApiClient().listHrVacations(filters),
  });
}

export function useApproveHrVacationOnBehalf() {
  const { isDemoMode } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; reason: string; comment?: string }) =>
      isDemoMode
        ? Promise.resolve(
            approveDemoHrVacationOnBehalf(input.id, {
              reason: input.reason,
              ...(input.comment ? { comment: input.comment } : {}),
            }),
          )
        : getApiClient().approveHrVacationOnBehalf(input.id, {
            reason: input.reason,
            ...(input.comment ? { comment: input.comment } : {}),
          }),
    onSuccess: () => invalidateHr(queryClient),
  });
}

export function useHrDocuments(filters: HrDocumentsQuery = {}) {
  const { isDemoMode } = useSession();
  return useQuery({
    queryKey: queryKeys.hr.documents(filters),
    queryFn: () =>
      isDemoMode ? listDemoHrDocuments(filters) : getApiClient().listHrDocuments(filters),
  });
}

export function useCreateHrDocument() {
  const { isDemoMode } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: HrCreateDocumentBody) =>
      isDemoMode
        ? Promise.resolve(createDemoHrDocument(body))
        : getApiClient().createHrDocument(body),
    onSuccess: () => invalidateHr(queryClient),
  });
}

export function useBatchHrDocuments() {
  const { isDemoMode } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: HrBatchDocumentsBody) =>
      isDemoMode
        ? Promise.resolve(batchDemoHrDocuments(body))
        : getApiClient().batchHrDocuments(body),
    onSuccess: () => invalidateHr(queryClient),
  });
}

export function useDistributeHrDocument() {
  const { isDemoMode } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; body: HrDistributeDocumentBody }) =>
      isDemoMode
        ? Promise.resolve(distributeDemoHrDocument(input.id, input.body))
        : getApiClient().distributeHrDocument(input.id, input.body),
    onSuccess: () => invalidateHr(queryClient),
  });
}

export function useHrDocumentAccesses(id: string | null) {
  const { isDemoMode } = useSession();
  return useQuery({
    queryKey: queryKeys.hr.documentAccesses(id ?? ""),
    enabled: Boolean(id),
    queryFn: () =>
      isDemoMode
        ? listDemoHrDocumentAccesses(id!)
        : getApiClient().listHrDocumentAccesses(id!),
  });
}

export function useHrAnnouncements(filters: HrAnnouncementsQuery = {}) {
  const { isDemoMode } = useSession();
  return useQuery({
    queryKey: queryKeys.hr.announcements(filters),
    queryFn: () =>
      isDemoMode
        ? listDemoHrAnnouncements(filters)
        : getApiClient().listHrAnnouncements(filters),
  });
}

export function useCreateHrAnnouncement() {
  const { isDemoMode } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: HrCreateAnnouncementBody) =>
      isDemoMode
        ? Promise.resolve(createDemoHrAnnouncement(body))
        : getApiClient().createHrAnnouncement(body),
    onSuccess: () => invalidateHr(queryClient),
  });
}

export function usePublishHrAnnouncement() {
  const { isDemoMode } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      isDemoMode
        ? Promise.resolve(publishDemoHrAnnouncement(id))
        : getApiClient().publishHrAnnouncement(id),
    onSuccess: () => invalidateHr(queryClient),
  });
}

export function useHrAnnouncementReach(id: string | null) {
  const { isDemoMode } = useSession();
  return useQuery({
    queryKey: queryKeys.hr.announcementReach(id ?? ""),
    enabled: Boolean(id),
    queryFn: () =>
      isDemoMode
        ? getDemoHrAnnouncementReach(id!)
        : getApiClient().getHrAnnouncementReach(id!),
  });
}

export function useHrAdmissions(filters: AdmissionsQuery = {}) {
  const { isDemoMode } = useSession();
  return useQuery({
    queryKey: queryKeys.hr.admissions(filters),
    queryFn: () =>
      isDemoMode
        ? listDemoHrAdmissions(filters)
        : getApiClient().listHrAdmissions(filters),
  });
}

export function useHrAdmission(id: string | null) {
  const { isDemoMode } = useSession();
  return useQuery({
    queryKey: queryKeys.hr.admission(id ?? ""),
    enabled: Boolean(id),
    queryFn: () =>
      isDemoMode ? getDemoHrAdmission(id!) : getApiClient().getHrAdmission(id!),
  });
}

export function useCreateHrAdmission() {
  const { isDemoMode } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateAdmissionBody) =>
      isDemoMode
        ? Promise.resolve(createDemoHrAdmission(body))
        : getApiClient().createHrAdmission(body),
    onSuccess: () => invalidateHr(queryClient),
  });
}

export function useValidateHrAdmissionItem() {
  const { isDemoMode } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      id: string;
      key: string;
      decision: "VALIDATED" | "REJECTED";
      comment?: string;
    }) =>
      isDemoMode
        ? Promise.resolve(
            validateDemoHrAdmissionItem(input.id, input.key, {
              decision: input.decision,
              ...(input.comment ? { comment: input.comment } : {}),
            }),
          )
        : getApiClient().validateHrAdmissionItem(input.id, input.key, {
            decision: input.decision,
            ...(input.comment ? { comment: input.comment } : {}),
          }),
    onSuccess: () => invalidateHr(queryClient),
  });
}

export function useHandoffHrAdmission() {
  const { isDemoMode } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      isDemoMode
        ? Promise.resolve(handoffDemoHrAdmission(id))
        : getApiClient().handoffHrAdmission(id),
    onSuccess: () => invalidateHr(queryClient),
  });
}

export function useHrOffboardings(filters: OffboardingsQuery = {}) {
  const { isDemoMode } = useSession();
  return useQuery({
    queryKey: queryKeys.hr.offboardings(filters),
    queryFn: () =>
      isDemoMode
        ? listDemoHrOffboardings(filters)
        : getApiClient().listHrOffboardings(filters),
  });
}

export function useHrOffboarding(id: string | null) {
  const { isDemoMode } = useSession();
  return useQuery({
    queryKey: queryKeys.hr.offboarding(id ?? ""),
    enabled: Boolean(id),
    queryFn: () =>
      isDemoMode ? getDemoHrOffboarding(id!) : getApiClient().getHrOffboarding(id!),
  });
}

export function useCreateHrOffboarding() {
  const { isDemoMode } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateOffboardingBody) =>
      isDemoMode
        ? Promise.resolve(createDemoHrOffboarding(body))
        : getApiClient().createHrOffboarding(body),
    onSuccess: () => invalidateHr(queryClient),
  });
}

export function useCompleteHrOffboardingItem() {
  const { isDemoMode } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; key: string; note?: string; waive?: boolean }) =>
      isDemoMode
        ? Promise.resolve(
            completeDemoHrOffboardingItem(input.id, input.key, {
              ...(input.note ? { note: input.note } : {}),
              ...(input.waive !== undefined ? { waive: input.waive } : {}),
            }),
          )
        : getApiClient().completeHrOffboardingItem(input.id, input.key, {
            ...(input.note ? { note: input.note } : {}),
            ...(input.waive !== undefined ? { waive: input.waive } : {}),
          }),
    onSuccess: () => invalidateHr(queryClient),
  });
}

export function useReturnHrOffboardingAsset() {
  const { isDemoMode } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      id: string;
      assetId: string;
      status?: "RETURNED" | "LOST" | "WAIVED";
    }) =>
      isDemoMode
        ? Promise.resolve(
            returnDemoHrOffboardingAsset(input.id, input.assetId, {
              ...(input.status ? { status: input.status } : {}),
            }),
          )
        : getApiClient().returnHrOffboardingAsset(input.id, input.assetId, {
            ...(input.status ? { status: input.status } : {}),
          }),
    onSuccess: () => invalidateHr(queryClient),
  });
}

export function useSaveHrOffboardingInterview() {
  const { isDemoMode } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      id: string;
      answers: Array<{ key: string; question: string; answer: string }>;
    }) =>
      isDemoMode
        ? Promise.resolve(
            saveDemoHrOffboardingInterview(input.id, { answers: input.answers }),
          )
        : getApiClient().saveHrOffboardingInterview(input.id, {
            answers: input.answers,
          }),
    onSuccess: () => invalidateHr(queryClient),
  });
}

export function useRevokeHrOffboardingAccess() {
  const { isDemoMode } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      isDemoMode
        ? Promise.resolve(revokeDemoHrOffboardingAccess(id))
        : getApiClient().revokeHrOffboardingAccess(id),
    onSuccess: () => invalidateHr(queryClient),
  });
}

export function useCompleteHrOffboarding() {
  const { isDemoMode } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      isDemoMode
        ? Promise.resolve(completeDemoHrOffboarding(id))
        : getApiClient().completeHrOffboarding(id),
    onSuccess: () => invalidateHr(queryClient),
  });
}
